if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.buyBlock = this.form?.closest('.buy-buttons-block');
      if (!this.form || !this.buyBlock) return;

      this.button = this.querySelector('[data-sticky-atc-button]');
      this.label = this.querySelector('[data-sticky-atc-label]');
      this.variantLabel = this.querySelector('[data-sticky-atc-variant]');
      this.image = this.querySelector('.sticky-atc__media img');
      this.optionNames = (this.dataset.options || '').split('|').filter(Boolean);
      this.picker = document.querySelector(`variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`);
      this.footer = document.querySelector('.shopify-section-group-footer-group, footer');

      this.sync = this.sync.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.button.addEventListener('click', this.onClick.bind(this));
      this.querySelector('[data-sticky-atc-change]')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToOptions();
      });

      // Like Edge: show once the main buy buttons have scrolled up out of view, hide while the footer is on screen
      this.buyButtonsPassed = false;
      this.footerInView = false;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.buyBlock) {
            this.buyButtonsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          } else {
            this.footerInView = entry.isIntersecting;
          }
        });
        this.toggle(this.buyButtonsPassed && !this.footerInView);
      });
      this.visibilityObserver.observe(this.buyBlock);
      if (this.footer) this.visibilityObserver.observe(this.footer);

      // Mirror whatever the theme does to the main button (text, sold out, loading, multipack swap)
      this.stateObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.syncFrame);
        this.syncFrame = requestAnimationFrame(this.sync);
      });
      this.stateObserver.observe(this.buyBlock, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled']
      });

      // variant-radios updates its currentVariant inside its own change handler, so read it after
      document.addEventListener('change', this.onVariantChange);

      this.updateVariant();
      this.sync();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.stateObserver?.disconnect();
      document.removeEventListener('change', this.onVariantChange);
    }

    toggle(show) {
      this.classList.toggle('sticky-atc--visible', show);
      this.toggleAttribute('inert', !show);
    }

    // The button that's actually in use: variant-bundler swaps in its own button for multipacks
    getSourceButton() {
      return this.buyBlock.querySelector('[data-multipack-atc]') || this.form.querySelector('[name="add"]');
    }

    sync() {
      const source = this.getSourceButton();
      if (!source) return;

      const soldOut = source.classList.contains('sp-hidden');
      const text = (source.querySelector('[data-atc-cta]') || source).textContent.trim();

      // The multipack button is disabled while no size is picked; keep ours clickable so it can take the shopper to the sizes
      const awaitingSize = source.disabled && this.needsSize() && text === 'Select a size';

      this.label.textContent = soldOut ? window.variantStrings?.soldOut || text : text;
      this.button.disabled = soldOut || (source.disabled && !awaitingSize);
      this.button.classList.toggle('loading', source.classList.contains('loading'));
      this.button.toggleAttribute('aria-disabled', source.hasAttribute('aria-disabled'));
    }

    onClick() {
      const source = this.getSourceButton();
      if (!source || this.button.disabled || source.classList.contains('loading')) return;

      // No size picked yet: the main ATC button's click handler shows the "select a size" error
      // (the multipack button can't, it's disabled), then take the shopper to it
      if (this.needsSize()) {
        this.form.querySelector('[name="add"]')?.click();
        this.scrollToOptions(document.querySelector('[data-size-selector]'));
        return;
      }

      // Runs all of the theme's existing ATC handlers (multipack, cart drawer)
      source.click();
    }

    needsSize() {
      const sizeSelector = document.querySelector('[data-size-selector]');
      return !!sizeSelector && !sizeSelector.querySelector('input[type="radio"]:checked');
    }

    scrollToOptions(target = this.picker) {
      if (!target) return;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
    }

    onVariantChange(event) {
      if (!this.picker || !this.picker.contains(event.target)) return;
      setTimeout(() => {
        this.updateVariant();
        this.sync();
      }, 0);
    }

    // "Color: Navy  Size: S", only for options the shopper actually picked (size starts unselected)
    updateVariant() {
      if (!this.picker) return;

      const selected = new Map();
      this.picker.querySelectorAll('input[type="radio"]:checked, select').forEach((input) => {
        if (this.optionNames.includes(input.name) && !selected.has(input.name)) selected.set(input.name, input.value);
      });
      if (this.variantLabel) {
        this.variantLabel.textContent = [...selected].map(([name, value]) => `${name}: ${value}`).join('  ·  ');
      }

      const imageSrc = this.picker.currentVariant?.featured_image?.src;
      if (this.image && imageSrc) {
        this.image.removeAttribute('srcset');
        this.image.src = `${imageSrc}${imageSrc.includes('?') ? '&' : '?'}width=160`;
      }
    }
  });
}
